import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@shared/constants'
import { authService } from '../services/authService'
import LoadingSpinner from '../components/LoadingSpinner'
import Nav from '../components/Nav'


/**
 * Manages account security settings including backup email and passkeys.
 * UI-ready for features awaiting backend implementation.
 */

export default function SecurityPage() {

    const { user, logout } = useAuthStore()
    const navigate = useNavigate()
    
    // Backup Email State
    const [backupEmail, setBackupEmail] = useState('')
    const [backupEmailStatus, setBackupEmailStatus] = useState<'none' | 'pending' | 'verified'>('none')
    const [isAddingBackupEmail, setIsAddingBackupEmail] = useState(false)
    const [backupEmailError, setBackupEmailError] = useState('')
    
    // WebAuthn State
    const [passkeys] = useState<Array<{ id: string; deviceName: string; createdAt: string }>>([])
    const [isAddingPasskey, setIsAddingPasskey] = useState(false)
    const [passkeyError, setPasskeyError] = useState('')


    /**
     * Loads user security settings on component mount.
     * Placeholder for backend integration.
     */

    useEffect(() => {

        loadSecuritySettings()
    }, [])


    /**
     * Fetches current security configuration from server.
     * To be implemented when backend endpoints are ready.
     */

    const loadSecuritySettings = async () => {

        // Security settings will be loaded when backend endpoints are implemented
        // Currently backup email and passkey features are UI-ready but waiting for backend
    }



    /**
     * Terminates user session and returns to login screen.
     * Clears authentication state from both client and server.
     */

    const handleLogout = async () => {

        try {
            await authService.logout()
            logout()
            navigate(ROUTES.LOGIN)
        } catch (error) {
            console.error('Logout failed:', error)
        }
    }


    /**
     * Adds and verifies backup email for account recovery.
     * Validates format and prevents duplicate primary email.
     */

    const handleAddBackupEmail = async () => {

        setBackupEmailError('')
        setIsAddingBackupEmail(true)
        
        try {
            // Validate email format
            if (!backupEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(backupEmail)) {
                throw new Error('Please enter a valid email address')
            }
            
            // Prevent same as primary
            if (backupEmail === user?.email) {
                throw new Error('Backup email cannot be the same as your primary email')
            }
            
            // Send verification email
            // await authService.addBackupEmail(backupEmail)
            setBackupEmailStatus('pending')
            alert('Verification email sent! Please check your inbox.')
        } catch (error: any) {
            setBackupEmailError(error.message || 'Failed to add backup email')
        } finally {
            setIsAddingBackupEmail(false)
        }
    }


    /**
     * Removes backup email from account after confirmation.
     * Reduces available recovery options.
     */

    const handleRemoveBackupEmail = async () => {

        if (!confirm('Remove backup email? This will remove one of your account recovery options.')) {
            return
        }
        
        try {
            // await authService.removeBackupEmail()
            setBackupEmail('')
            setBackupEmailStatus('none')
        } catch (error) {
            alert('Failed to remove backup email')
        }
    }



    /**
     * Registers new passkey using WebAuthn API for biometric auth.
     * Checks browser support before initiating registration flow.
     */

    const handleAddPasskey = async () => {

        setPasskeyError('')
        setIsAddingPasskey(true)
        
        try {
            // Check browser support
            if (!window.PublicKeyCredential) {
                throw new Error('Your browser does not support passkeys. Please use a modern browser like Chrome, Safari, or Edge.')
            }
            
            // Get registration options from server
            // const options = await authService.getPasskeyRegistrationOptions()
            
            // Create credential using WebAuthn API
            // const credential = await navigator.credentials.create({
            //   publicKey: options
            // })
            
            // Send credential to server for verification
            // await authService.registerPasskey(credential)
            
            alert('Passkey registered successfully! You can now use it to sign in.')
            await loadSecuritySettings()
        } catch (error: any) {
            setPasskeyError(error.message || 'Failed to add passkey')
        } finally {
            setIsAddingPasskey(false)
        }
    }


    /**
     * Removes passkey after user confirmation.
     * Permanently disables that authentication method.
     */

    const handleRemovePasskey = async (_passkeyId: string) => {

        if (!confirm('Remove this passkey? You will no longer be able to use it to sign in.')) {
            return
        }
        
        try {
            // await authService.removePasskey(passkeyId)
            await loadSecuritySettings()
        } catch (error) {
            alert('Failed to remove passkey')
        }
    }


    return (
        <div className="min-h-screen bg-slate-900 text-white pb-20 md:pb-8 md:pt-28 transition-colors duration-300">
            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <h2 className="text-3xl font-bold text-white">Security Settings</h2>
                    <p className="mt-2 text-slate-400">
                        Manage your account security and recovery options
                    </p>
                </div>

                <div className="space-y-6">
                    {/* Backup Email Section */}
                    <div className="bg-slate-900/50 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-800 p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-1">
                                    Backup Email Address
                                </h3>
                                <p className="text-sm text-slate-400">
                                    Add a backup email to recover your account if you lose access to your primary email
                                </p>
                            </div>
                            {backupEmailStatus === 'verified' && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-950/30 text-emerald-400 border border-emerald-900/50">
                                    ✓ Verified
                                </span>
                            )}
                            {backupEmailStatus === 'pending' && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-950/30 text-amber-400 border border-amber-900/50">
                                    ⏳ Pending
                                </span>
                            )}
                        </div>

                        {backupEmailStatus === 'none' && (
                            <div className="mt-4">
                                <div className="flex space-x-3">
                                    <input
                                        type="email"
                                        value={backupEmail}
                                        onChange={(e) => setBackupEmail(e.target.value)}
                                        placeholder="backup@example.com"
                                        className="flex-1 px-4 py-2 bg-slate-950 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition"
                                        disabled={isAddingBackupEmail}
                                    />
                                    <button
                                        onClick={handleAddBackupEmail}
                                        disabled={isAddingBackupEmail}
                                        className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-500 focus:ring-4 focus:ring-primary-900 transition disabled:opacity-50"
                                    >
                                        {isAddingBackupEmail ? 'Adding...' : 'Add Email'}
                                    </button>
                                </div>
                                {backupEmailError && (
                                    <p className="mt-2 text-sm text-rose-400">{backupEmailError}</p>
                                )}
                            </div>
                        )}

                        {backupEmailStatus === 'pending' && (
                            <div className="mt-4 bg-amber-950/20 border border-amber-900/30 rounded-lg p-4">
                                <p className="text-sm text-amber-200 mb-3">
                                    <strong>Verification pending:</strong> We sent a verification link to <strong>{backupEmail}</strong>. Please check your inbox.
                                </p>
                                <button
                                    onClick={handleRemoveBackupEmail}
                                    className="text-sm text-rose-400 hover:text-rose-300 font-medium transition"
                                >
                                    Cancel and remove
                                </button>
                            </div>
                        )}

                        {backupEmailStatus === 'verified' && (
                            <div className="mt-4 bg-emerald-950/20 border border-emerald-900/30 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-emerald-200">
                                            <strong>{backupEmail}</strong>
                                        </p>
                                        <p className="text-xs text-emerald-400/70 mt-1">
                                            This email can be used to recover your account
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleRemoveBackupEmail}
                                        className="text-sm text-rose-400 hover:text-rose-300 font-medium transition"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* WebAuthn/Passkeys Section */}
                    <div className="bg-slate-900/50 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-800 p-6">
                        <div className="mb-4">
                            <h3 className="text-lg font-semibold text-white mb-1">
                                Passkeys (Biometric Authentication)
                            </h3>
                            <p className="text-sm text-slate-400">
                                Use your fingerprint, face, or device PIN to sign in quickly and securely. Passkeys are phishing-resistant and work across your devices.
                            </p>
                        </div>

                        {passkeys.length === 0 && (
                            <div className="mt-4 bg-blue-950/20 border border-blue-900/30 rounded-lg p-4 mb-4">
                                <p className="text-sm text-blue-200">
                                    <strong>No passkeys yet.</strong> Add a passkey to enable fast, secure sign-in using your fingerprint or face.
                                </p>
                            </div>
                        )}

                        {passkeys.length > 0 && (
                            <div className="mt-4 space-y-3 mb-4">
                                {passkeys.map((passkey) => (
                                    <div
                                        key={passkey.id}
                                        className="flex items-center justify-between p-4 border border-slate-700 bg-slate-950/50 rounded-lg"
                                    >
                                        <div>
                                            <p className="font-medium text-white">{passkey.deviceName}</p>
                                            <p className="text-xs text-slate-500">
                                                Added {new Date(passkey.createdAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => handleRemovePasskey(passkey.id)}
                                            className="text-sm text-rose-400 hover:text-rose-300 font-medium transition"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button
                            onClick={handleAddPasskey}
                            disabled={isAddingPasskey}
                            className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-500 focus:ring-4 focus:ring-primary-900 transition disabled:opacity-50 flex items-center justify-center"
                        >
                            {isAddingPasskey ? (
                                <>
                                    <LoadingSpinner size="small" />
                                    <span className="ml-2">Adding passkey...</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Add Passkey
                                </>
                            )}
                        </button>
                        {passkeyError && (
                            <p className="mt-2 text-sm text-rose-400">{passkeyError}</p>
                        )}

                        <div className="mt-4 bg-slate-800/50 rounded-lg p-4 border border-slate-800">
                            <p className="text-xs text-slate-400">
                                <strong>Compatible devices:</strong> iPhone (iOS 16+), Android (Android 9+), Windows (10+), macOS (Ventura+). 
                                Your passkey is stored securely on your device and syncs with your Apple, Google, or Microsoft account.
                            </p>
                        </div>
                    </div>

                    {/* Password Section */}
                    <div className="bg-slate-900/50 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-800 p-6">
                        <h3 className="text-lg font-semibold text-white mb-1">
                            Password
                        </h3>
                        <p className="text-sm text-slate-400 mb-4">
                            Change your password or reset it if you've forgotten it
                        </p>
                        <button
                            onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                            className="px-6 py-2 border border-slate-600 text-slate-300 rounded-lg font-medium hover:bg-slate-800 hover:text-white transition"
                        >
                            Change Password
                        </button>
                    </div>
                </div>
            </main>

            <Nav email={user?.email} onLogout={handleLogout} />
        </div>
    )

}
